/**
 * Statement Duplicate Cleanup diagnostic card.
 *
 * CLAUDE.md task: the fan-out backfill re-parsed legacy CWC statement
 * emails and, on AI text drift in the parsed property name across two
 * separate parses of the identical PDF, missed matching the existing doc
 * and created a duplicate. This card finds and cleans up the duplicates
 * that bug already created (the ingestion path itself is already fixed --
 * see routes/management_statements.py -- so this is a one-time backlog,
 * not a recurring job).
 *
 * Grouping: resolved property (property_ref, else normalized property
 * name) + statement month -- source email is no longer part of the key,
 * since real prod twins do not reliably share one (a re-sent/re-forwarded
 * email, a second parse run under a new message id).
 * Keep-rule: a twin already referenced by a real statement_payout_matches
 * doc wins first (keeping the other would silently orphan that link),
 * then reconciliation-clean, then Paid-tagged, then newest. Losers are
 * soft-excluded (never hard-deleted, see excluded_as_duplicate on the doc)
 * so they drop out of every list/total that already respects the
 * exclusion filter, but remain recoverable.
 *
 * Ambiguous groups (two twins each with their own payout match, or
 * amounts that disagree beyond a penny) are never auto-resolved -- listed
 * read-only under "Needs Human Review" instead.
 *
 * Preview-then-apply, same pattern as the other audit cards on this page.
 */

import { useState } from 'react';
import { Loader2, Search, CheckCircle2, AlertTriangle, Copy, Eye } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { statementDuplicateCleanupAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function formatDate(isoStr) {
  if (!isoStr) return '--';
  try {
    return new Date(isoStr).toLocaleString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch (_) {
    return isoStr;
  }
}

function DocRow({ doc, role }) {
  var isWinner = role === 'winner';
  return (
    <tr className={'border-b border-border/50' + (isWinner ? ' bg-green-50 dark:bg-green-950/20' : '')}>
      <td className="py-1.5 pr-3">
        <Badge className={isWinner
          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 text-[10px]'
          : 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400 text-[10px]'
        }>
          {isWinner ? 'keep' : 'exclude'}
        </Badge>
      </td>
      <td className="py-1.5 pr-3 font-mono text-xs">{doc.statement_id}</td>
      <td className="py-1.5 pr-3 text-xs">{doc.property_name_raw}</td>
      <td className="py-1.5 pr-3 text-xs">{doc.statement_month}</td>
      <td className="py-1.5 pr-3 text-xs">{doc.reconciliation_status || '--'}</td>
      <td className="py-1.5 pr-3 text-xs text-muted-foreground">{formatDate(doc.created_at)}</td>
    </tr>
  );
}

function DuplicateGroup({ group }) {
  return (
    <div className="border border-border rounded p-3">
      <p className="text-xs text-muted-foreground mb-2 font-mono">{group.group_key}</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left">
              <th className="py-1.5 pr-3 font-medium"></th>
              <th className="py-1.5 pr-3 font-medium">Statement ID</th>
              <th className="py-1.5 pr-3 font-medium">Property</th>
              <th className="py-1.5 pr-3 font-medium">Month</th>
              <th className="py-1.5 pr-3 font-medium">Reconciliation</th>
              <th className="py-1.5 pr-3 font-medium">Created</th>
            </tr>
          </thead>
          <tbody>
            <DocRow doc={group.winner} role="winner" />
            {group.losers.map(function (doc) {
              return <DocRow key={doc.id} doc={doc} role="loser" />;
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function HumanReviewRow({ doc }) {
  return (
    <tr className="border-b border-border/50">
      <td className="py-1.5 pr-3 font-mono text-xs">{doc.statement_id}</td>
      <td className="py-1.5 pr-3 text-xs">{doc.property_name_raw}</td>
      <td className="py-1.5 pr-3 text-xs">{doc.statement_month}</td>
      <td className="py-1.5 pr-3 text-xs">{doc.reconciliation_status || '--'}</td>
      <td className="py-1.5 pr-3 text-xs">{doc.has_payout_match ? 'yes' : 'no'}</td>
      <td className="py-1.5 pr-3 text-xs text-muted-foreground">{doc.income_net}</td>
    </tr>
  );
}

function HumanReviewGroup({ group }) {
  return (
    <div className="border border-amber-300 dark:border-amber-800 rounded p-3">
      <p className="text-xs text-muted-foreground mb-2 font-mono">
        {group.group_key} -- {group.reason.split('_').join(' ')}
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left">
              <th className="py-1.5 pr-3 font-medium">Statement ID</th>
              <th className="py-1.5 pr-3 font-medium">Property</th>
              <th className="py-1.5 pr-3 font-medium">Month</th>
              <th className="py-1.5 pr-3 font-medium">Reconciliation</th>
              <th className="py-1.5 pr-3 font-medium">Has Match</th>
              <th className="py-1.5 pr-3 font-medium">Income Net</th>
            </tr>
          </thead>
          <tbody>
            {group.members.map(function (doc) {
              return <HumanReviewRow key={doc.id} doc={doc} />;
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function StatementDuplicateCleanupPanel() {
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(false);
  var [applying, setApplying] = useState(false);
  var [applyResult, setApplyResult] = useState(null);

  var loadPreview = async function () {
    setLoading(true);
    setApplyResult(null);
    try {
      var res = await statementDuplicateCleanupAPI.preview();
      setData(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  var handleApply = async function () {
    setApplying(true);
    try {
      var res = await statementDuplicateCleanupAPI.apply();
      var result = res.data;
      toast.success(
        'Excluded ' + result.docs_excluded_count + ' duplicate document(s) across ' +
        result.groups_resolved + ' group(s). Run: ' + result.run_id
      );
      setApplyResult(result);
      await loadPreview();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplying(false);
    }
  };

  var groupsCount = data ? data.groups_count : 0;
  var docsCount = data ? data.docs_to_exclude_count : 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Copy className="h-4 w-4" />
          Statement Duplicate Cleanup
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Finds management statement documents duplicated by the AI-text-drift
          dedup bug (same source email, resolved property, and month, minted
          twice because the parsed property name text differed slightly
          between two parses of the identical PDF). Within each group the
          document whose reconciliation passed is kept; the other is
          soft-excluded, never hard-deleted, and drops out of every list and
          total that reads the exclusion filter.
        </p>

        <div className="flex gap-2">
          <Button onClick={loadPreview} disabled={loading}>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Search className="h-4 w-4 mr-2" />
            )}
            Preview
          </Button>

          {data && groupsCount > 0 && (
            <Button
              variant="destructive"
              onClick={handleApply}
              disabled={applying || loading}
            >
              {applying ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Copy className="h-4 w-4 mr-2" />
              )}
              Exclude {docsCount} Duplicate{docsCount !== 1 ? 's' : ''}
            </Button>
          )}
        </div>

        {data && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm">
              {groupsCount === 0 ? (
                <>
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <span className="text-muted-foreground">No duplicate statements found</span>
                </>
              ) : (
                <>
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <span>
                    {groupsCount} duplicate group{groupsCount !== 1 ? 's' : ''}, {docsCount} document{docsCount !== 1 ? 's' : ''} to exclude
                  </span>
                </>
              )}
            </div>

            {data.groups && data.groups.length > 0 && (
              <div className="space-y-2">
                {data.groups.map(function (group) {
                  return <DuplicateGroup key={group.group_key} group={group} />;
                })}
              </div>
            )}

            {data.human_review_count > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Eye className="h-4 w-4 text-amber-600" />
                  <span>
                    {data.human_review_count} group{data.human_review_count !== 1 ? 's' : ''} need
                    {data.human_review_count === 1 ? 's' : ''} human review (never auto-resolved)
                  </span>
                </div>
                {data.human_review.map(function (group) {
                  return <HumanReviewGroup key={group.group_key} group={group} />;
                })}
              </div>
            )}

          </div>
        )}

        {applyResult && (
          <p className="text-xs text-muted-foreground">
            Last apply: {applyResult.docs_excluded_count} excluded at {formatDate(applyResult.applied_at)} (run {applyResult.run_id})
          </p>
        )}
      </CardContent>
    </Card>
  );
}
